import { Injectable, inject } from '@angular/core';
import { LoggerService } from './logger.service';
import { getSanitizedUrl, isHttpError } from './logger-helper';

/**
 * Bootstrap service that initializes global error event listeners.
 * This captures runtime errors and unhandled promise rejections
 * that occur outside Angular's zone (e.g., failed dynamic imports).
 *
 * This service is automatically instantiated by the dependency injector
 * when the app module is bootstrapped (see app.module.ts providers).
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorBootstrapService {
  private initialized = false;
  private readonly logger = inject(LoggerService);

  constructor() {
    this.initializeGlobalErrorHandlers();
  }

  /**
   * Set up global error event listeners
   */
  private initializeGlobalErrorHandlers(): void {
    if (this.initialized) {
      return;
    }

    // Capture synchronous runtime errors (including failed dynamic imports)
    globalThis.addEventListener('error', (event: ErrorEvent) => {
      this.logger.log({
        type: 'error',
        context: `Browser Runtime Error (${event.filename}:${event.lineno}:${event.colno})`,
        message: event.error?.message || event.message,
        stack: event.error?.stack,
        userAgent: globalThis.navigator.userAgent,
        url: getSanitizedUrl(),
      });
    });

    // Capture unhandled promise rejections (async errors not caught by try/catch)
    globalThis.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      // Skip HTTP errors - they're already logged by the logging-interceptor
      if (isHttpError(event.reason)) {
        return;
      }

      this.logger.log({
        type: 'unhandledRejection',
        context: 'Browser Unhandled Promise Rejection',
        reason: event.reason instanceof Error ? event.reason.message : String(event.reason),
        stack: event.reason instanceof Error ? event.reason.stack : undefined,
        userAgent: globalThis.navigator.userAgent,
        url: getSanitizedUrl(),
      });
    });

    this.initialized = true;
  }
}
